import {t} from '@lingui/macro'
import queryString from 'qs'
import useSWR from 'swr'

import config from 'config'
import {reportError} from '~/error/report'
import {useActionWithFeedback} from '~/generic-ui/use-action-with-feedback/use-action-with-feedback'
import Country from '~/locale/country'
import {useToaster} from '~/shell/toaster'
import {getCountry, useLoggedInUser} from '~/shell/user'

import {getDeimos, postDeimos} from './helpers'
import {useCompanyTopUpSettings} from './wallet'

import {request} from '../api'
import {RequestScope} from '../types'
import type {DirectDebitAgreement} from '../types/direct-debit'
import type {DirectDebitAgreementIdResponse} from '../types/wallet'

export function useDirectDebitAgreementId() {
    const user = useLoggedInUser()
    const country = getCountry(user)
    const isGBCompany = country === Country.GB

    const isAbleToCall = Boolean(isGBCompany && user.companyId)

    const queryParams = queryString.stringify({companyId: user.companyId})

    return useSWR<DirectDebitAgreementIdResponse>(
        isAbleToCall ? `/wallet-management/rest/v4/bacs/agreement-id?${queryParams}` : null,
        postDeimos,
    )
}

export function useBacsAgreementConsentText() {
    const user = useLoggedInUser()
    const country = getCountry(user)
    const isGBCompany = country === Country.GB

    const isAbleToCall = Boolean(isGBCompany && user.companyId)

    const queryParams = queryString.stringify({companyId: user.companyId})

    const result = useSWR<string, Error>(
        isAbleToCall ? `/wallet-management/rest/v4/bacs/consent-text?${queryParams}` : null,
        getDeimos,
    )

    return {
        ...result,
        isLoading: isAbleToCall && !result.error && !result.data,
    }
}

export function useDirectDebitAgreement() {
    const {showToast} = useToaster()

    const user = useLoggedInUser()
    const country = getCountry(user)

    const {
        data: companyTopUpSettings,
        mutations: {disableAutoTopUp},
    } = useCompanyTopUpSettings()

    const {data, error, mutate} = useSWR<DirectDebitAgreement, Error>(
        user.companyId ? `/wallet-management/rest/v4/companies/${user.companyId}/agreements` : null,
        getDeimos,
        {revalidateIfStale: false},
    )
    const revalidateAgreement = async function () {
        return mutate()
    }

    async function deleteAgreementInner() {
        if (!user.companyId || !country) {
            return
        }

        try {
            if (companyTopUpSettings?.autoTopupStatus === 'ENABLED') {
                await disableAutoTopUp()
            }

            await deleteDirectDebitAgreement(user.companyId, country)
            showToast(t`Agreement deleted`)
            await mutate()
        } catch (err) {
            reportError(err)
            showToast(t`Failed to delete the agreement`, {level: 'error'})
        }
    }

    const deleteAgreement = useActionWithFeedback(
        deleteAgreementInner,
        () => ({
            confirmTitle: t`Delete agreement?`,
            confirmLabel: t`Confirm`,
            confirmDescription: t`You will no longer be able to top-up via Direct Debit or use auto top-up until you add a new agreement. This may take up to 6 business days.`,
        }),
        {throwOnError: true},
    )

    return {
        error,
        directDebitAgreement: data,
        isDirectDebitEnabled: !error && data?.status === 'READY',
        isLoading: user.companyId && data === undefined && error === undefined,
        mutations: {deleteAgreement},
        revalidateAgreement,
    }
}

async function deleteDirectDebitAgreement(companyId: string, country: Country) {
    return request(
        `${config.endpoints.api}/wallet-management/rest/v4/companies/${companyId}/agreements/${country}/cancel`,
        {
            auth: 'elevated',
            scope: RequestScope.PERMISSION,
            method: 'POST',
        },
    )
}
